import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Home, ShoppingBag, ListChecks, Users, Dice5, Sparkles } from 'lucide-react';

const CONFIG = {
  marketplace: {
    title: 'Marketplace',
    subtitle: 'Spend your coins on real rewards',
    Icon: ShoppingBag,
    gradient: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)',
    iconColor: '#3F2A00',
    accent: '#F59E0B',
    blurb: 'Browse and redeem prizes, perks, and bonus credits using the coins you’ve earned.',
  },
  tiers: {
    title: 'Daily Task & Tiers',
    subtitle: 'Climb the VIP ladder one task at a time',
    Icon: ListChecks,
    gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    iconColor: '#FFFFFF',
    accent: '#10B981',
    blurb: 'Complete daily missions to gain XP, unlock higher tiers, and earn exclusive perks.',
  },
  referral: {
    title: 'Referral & Earn',
    subtitle: 'Invite friends, earn together',
    Icon: Users,
    gradient: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
    iconColor: '#FFFFFF',
    accent: '#8B5CF6',
    blurb: 'Share your referral link. When friends join and play, you both get bonuses.',
  },
  roulette: {
    title: 'Daily Task Roulette',
    subtitle: 'One free spin every day',
    Icon: Dice5,
    gradient: 'linear-gradient(135deg, #EF4444 0%, #B91C1C 100%)',
    iconColor: '#FFFFFF',
    accent: '#EF4444',
    blurb: 'Spin the wheel daily to win bonus coins, free credits, or surprise rewards.',
  },
};

export default function ExploreSubPage({ section }) {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const cfg = CONFIG[section];

  const homePath = tenantSlug ? `/${tenantSlug}/app` : '/app';
  const explorePath = tenantSlug ? `/${tenantSlug}/app/explore` : '/app/explore';

  if (!cfg) {
    return (
      <div className="player-app min-h-screen flex items-center justify-center text-white">
        <p>Unknown section.</p>
      </div>
    );
  }

  const { Icon } = cfg;

  return (
    <div
      className="player-app min-h-screen pb-12"
      style={{ color: '#FFFFFF' }}
      data-testid={`explore-sub-${section}`}
    >
      {/* Header */}
      <div
        className="sticky top-0 z-30 px-4 sm:px-6 py-4 flex items-center gap-3"
        style={{
          background: 'rgba(6,23,14,0.85)',
          backdropFilter: 'blur(18px) saturate(160%)',
          WebkitBackdropFilter: 'blur(18px) saturate(160%)',
          borderBottom: `1px solid ${cfg.accent}55`,
        }}
      >
        <button
          onClick={() => navigate(explorePath)}
          className="w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95"
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#FFFFFF',
          }}
          data-testid={`${section}-back`}
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1
            className="display font-extrabold text-xl sm:text-2xl flex items-center gap-2"
            style={{ color: '#FFFFFF' }}
          >
            <span
              className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ background: cfg.gradient }}
            >
              <Icon className="w-4 h-4" style={{ color: cfg.iconColor }} strokeWidth={2.6} />
            </span>
            {cfg.title}
          </h1>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
            {cfg.subtitle}
          </p>
        </div>
      </div>

      {/* Body — placeholder card */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div
          className="rounded-3xl p-8 sm:p-12 text-center"
          style={{
            background: `linear-gradient(180deg, ${cfg.accent}1F, rgba(255,255,255,0.02))`,
            border: `1px dashed ${cfg.accent}66`,
          }}
        >
          <div
            className="w-20 h-20 rounded-2xl mx-auto mb-5 flex items-center justify-center"
            style={{
              background: cfg.gradient,
              boxShadow: `0 12px 30px ${cfg.accent}77, inset 0 1px 0 rgba(255,255,255,0.4)`,
            }}
          >
            <Icon className="w-10 h-10" style={{ color: cfg.iconColor }} strokeWidth={2.5} />
          </div>
          <h2
            className="display font-extrabold text-2xl sm:text-3xl mb-2"
            style={{ color: '#FFFFFF' }}
          >
            Coming soon
          </h2>
          <p
            className="text-sm sm:text-base max-w-lg mx-auto"
            style={{ color: 'rgba(255,255,255,0.78)' }}
          >
            {cfg.blurb}
          </p>
          <div
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs"
            style={{
              background: 'rgba(34,255,136,0.10)',
              border: '1px solid rgba(34,255,136,0.30)',
              color: '#86EFAC',
            }}
          >
            <Sparkles className="w-4 h-4" />
            We’ll plug in the real content here next.
          </div>
        </div>
      </div>
    </div>
  );
}
